import { useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Separator } from "@/components/ui/separator";
import { Badge } from "@/components/ui/badge";
import { useToast } from "@/components/ui/use-toast";
import { Loader2, Brain, Sparkles, CheckCircle, AlertCircle, Globe } from "lucide-react";
import CustomPromptInput from "./CustomPromptInput";
import WebContentGenerator from "./WebContentGenerator";

interface Topic {
  id: string;
  name: string;
  parent_topic_id: string | null;
}

interface GeneratedMCQ {
  question: string;
  options: string[];
  correct_index: number;
  explanation: string;
  tags: string[];
}

const fetchTopics = async (): Promise<Topic[]> => {
  const { data, error } = await supabase.from("topics").select("*").order("name");
  if (error) throw error;
  return data || [];
};

export default function AIMCQGenerator() {
  const { toast } = useToast();
  const [topic, setTopic] = useState("");
  const [subtopic, setSubtopic] = useState("");
  const [difficulty, setDifficulty] = useState("3");
  const [count, setCount] = useState("5");
  const [customPrompt, setCustomPrompt] = useState("");
  const [generatedMCQs, setGeneratedMCQs] = useState<GeneratedMCQ[]>([]);
  const [selectedMCQs, setSelectedMCQs] = useState<Set<number>>(new Set());
  const [sourceInfo, setSourceInfo] = useState<{ url?: string; title?: string } | null>(null);

  const { data: topics = [] } = useQuery({
    queryKey: ["topics"],
    queryFn: fetchTopics,
  });

  const generateMutation = useMutation({
    mutationFn: async (params: { topic: string; subtopic?: string; difficulty: number; count: number; customPrompt?: string }) => {
      const { data, error } = await supabase.functions.invoke('ai-generate-mcqs', {
        body: params
      });
      if (error) throw error;
      return data;
    },
    onSuccess: (data) => {
      setGeneratedMCQs(data.mcqs || []);
      setSelectedMCQs(new Set(Array.from({ length: data.mcqs?.length || 0 }, (_, i) => i)));
      toast({
        title: "MCQs Generated Successfully",
        description: `Generated ${data.mcqs?.length || 0} questions using ${data.metadata?.tokensUsed || 0} tokens`,
      });
    },
    onError: (error) => {
      toast({
        title: "Generation Failed",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const importMutation = useMutation({
    mutationFn: async (mcqs: GeneratedMCQ[]) => {
      const selectedTopicData = topics.find(t => t.name === topic);
      const mcqsToImport = mcqs.map(mcq => ({
        stem: mcq.question,
        option_a: mcq.options[0] || '',
        option_b: mcq.options[1] || '',
        option_c: mcq.options[2] || '',
        option_d: mcq.options[3] || null,
        option_e: mcq.options[4] || null,
        correct_option: ['A', 'B', 'C', 'D', 'E'][mcq.correct_index] as 'A' | 'B' | 'C' | 'D' | 'E',
        explanation: mcq.explanation,
        difficulty: parseInt(difficulty),
        tags: mcq.tags,
        topic_id: selectedTopicData?.id || null,
      }));

      const { error } = await supabase.from("mcqs").insert(mcqsToImport);
      if (error) throw error;
      return mcqsToImport.length;
    },
    onSuccess: (count) => {
      toast({
        title: "MCQs Imported Successfully",
        description: `Successfully imported ${count} questions to the database`,
      });
      setGeneratedMCQs([]);
      setSelectedMCQs(new Set());
    },
    onError: (error) => {
      toast({
        title: "Import Failed",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const handleGenerate = () => {
    if (!topic.trim()) {
      toast({
        title: "Topic Required",
        description: "Please enter a topic for MCQ generation",
        variant: "destructive",
      });
      return;
    }

    generateMutation.mutate({
      topic: topic.trim(),
      subtopic: subtopic.trim() || undefined,
      difficulty: parseInt(difficulty),
      count: parseInt(count),
      customPrompt: customPrompt.trim() || undefined,
    });
  };

  const handleImportSelected = () => {
    const selectedMCQsArray = generatedMCQs.filter((_, index) => selectedMCQs.has(index));
    if (selectedMCQsArray.length === 0) {
      toast({
        title: "No MCQs Selected",
        description: "Please select at least one MCQ to import",
        variant: "destructive",
      });
      return;
    }
    importMutation.mutate(selectedMCQsArray);
  };

  const toggleMCQSelection = (index: number) => {
    const newSelected = new Set(selectedMCQs);
    if (newSelected.has(index)) {
      newSelected.delete(index);
    } else {
      newSelected.add(index);
    }
    setSelectedMCQs(newSelected);
  };

  const handleWebContentGenerated = (data: any) => {
    setGeneratedMCQs(data.mcqs || []);
    setSelectedMCQs(new Set(Array.from({ length: data.mcqs?.length || 0 }, (_, i) => i)));
    setSourceInfo({
      url: data.sourceUrl,
      title: data.sourceTitle
    });
    toast({
      title: "MCQs Generated from Web Content",
      description: `Generated ${data.mcqs?.length || 0} questions from web source`,
    });
  };

  return (
    <div className="space-y-6">
      <Card className="border-primary/20 bg-gradient-to-br from-background to-primary/5">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Brain className="h-5 w-5 text-primary" />
            AI MCQ Generator
          </CardTitle>
        </CardHeader>
        <CardContent>
          <Tabs defaultValue="manual" className="w-full">
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="manual" className="flex items-center gap-2">
                <Brain className="h-4 w-4" />
                Manual Input
              </TabsTrigger>
              <TabsTrigger value="web" className="flex items-center gap-2">
                <Globe className="h-4 w-4" />
                From Web URL
              </TabsTrigger>
            </TabsList>
            
            <TabsContent value="manual" className="space-y-4 mt-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="topic">Medical Topic *</Label>
                  <Input
                    id="topic"
                    value={topic}
                    onChange={(e) => setTopic(e.target.value)}
                    placeholder="e.g., Cardiology, Diabetes, Pneumonia"
                    className="border-primary/20"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="subtopic">Subtopic (Optional)</Label>
                  <Input
                    id="subtopic"
                    value={subtopic}
                    onChange={(e) => setSubtopic(e.target.value)}
                    placeholder="e.g., Acute MI, Type 2 DM"
                    className="border-primary/20"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="difficulty">Difficulty Level</Label>
                  <Select value={difficulty} onValueChange={setDifficulty}>
                    <SelectTrigger className="border-primary/20">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="1">1 - Basic</SelectItem>
                      <SelectItem value="2">2 - Easy</SelectItem>
                      <SelectItem value="3">3 - Moderate</SelectItem>
                      <SelectItem value="4">4 - Advanced</SelectItem>
                      <SelectItem value="5">5 - Expert</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="count">Number of Questions</Label>
                  <Select value={count} onValueChange={setCount}>
                    <SelectTrigger className="border-primary/20">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="3">3 Questions</SelectItem>
                      <SelectItem value="5">5 Questions</SelectItem>
                      <SelectItem value="10">10 Questions</SelectItem>
                      <SelectItem value="15">15 Questions</SelectItem>
                      <SelectItem value="20">20 Questions</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <CustomPromptInput
                value={customPrompt}
                onChange={setCustomPrompt}
                contentType="mcq"
                placeholder="Add custom instructions for MCQ generation (e.g., 'Focus on clinical scenarios', 'Include diagnostic imaging questions')..."
              />

              <Button 
                onClick={handleGenerate} 
                disabled={generateMutation.isPending}
                className="w-full bg-gradient-to-r from-primary to-primary/80 hover:from-primary/90 hover:to-primary/70"
              >
                {generateMutation.isPending ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Generating MCQs...
                  </>
                ) : (
                  <>
                    <Sparkles className="mr-2 h-4 w-4" />
                    Generate MCQs with AI
                  </>
                )}
              </Button>
            </TabsContent>

            <TabsContent value="web" className="mt-4">
              <WebContentGenerator 
                onContentGenerated={handleWebContentGenerated}
                generationType="mcq"
              />
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>

      {generatedMCQs.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center justify-between">
              <div className="space-y-1">
                <span>Generated MCQs ({generatedMCQs.length})</span>
                {sourceInfo && (
                  <div className="text-sm text-muted-foreground flex items-center gap-1">
                    <Globe className="h-3 w-3" />
                    Source: {sourceInfo.title || sourceInfo.url}
                  </div>
                )}
              </div>
              <Button 
                onClick={handleImportSelected}
                disabled={importMutation.isPending || selectedMCQs.size === 0}
                variant="outline"
                className="border-primary text-primary hover:bg-primary hover:text-primary-foreground"
              >
                {importMutation.isPending ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Importing...
                  </>
                ) : (
                  <>
                    <CheckCircle className="mr-2 h-4 w-4" />
                    Import Selected ({selectedMCQs.size})
                  </>
                )}
              </Button>
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {generatedMCQs.map((mcq, index) => (
              <Card key={index} className={`border ${selectedMCQs.has(index) ? 'border-primary bg-primary/5' : 'border-muted'}`}>
                <CardHeader className="pb-3">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <p className="font-medium text-sm mb-2">Question {index + 1}</p>
                      <p className="text-foreground">{mcq.question}</p>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => toggleMCQSelection(index)}
                      className={selectedMCQs.has(index) ? 'text-primary' : 'text-muted-foreground'}
                    >
                      {selectedMCQs.has(index) ? <CheckCircle className="h-4 w-4" /> : <AlertCircle className="h-4 w-4" />}
                    </Button>
                  </div>
                </CardHeader>
                <CardContent className="pt-0 space-y-3">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                    {mcq.options.map((option, optionIndex) => (
                      <div
                        key={optionIndex}
                        className={`p-2 rounded text-sm ${
                          optionIndex === mcq.correct_index
                            ? 'bg-success/20 border border-success/40 text-success-foreground'
                            : 'bg-muted'
                        }`}
                      >
                        <span className="font-medium">{String.fromCharCode(65 + optionIndex)}.</span> {option}
                      </div>
                    ))}
                  </div>
                  
                  <Separator />
                  
                  <div className="space-y-2">
                    <p className="text-sm font-medium">Explanation:</p>
                    <p className="text-sm text-muted-foreground">{mcq.explanation}</p>
                  </div>
                  
                  {mcq.tags.length > 0 && (
                    <div className="flex flex-wrap gap-1">
                      {mcq.tags.map((tag, tagIndex) => (
                        <Badge key={tagIndex} variant="secondary" className="text-xs">
                          {tag}
                        </Badge>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}
          </CardContent>
        </Card>
      )}
    </div>
  );
}